import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

export const REVENUE_ADMIN_EMAILS = ['cole@keyflo.ai', 'james@keyflo.ai'];

export const DEV_ADMIN_EMAILS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];

export const DEMO_CONSOLE_EMAILS = [...DEV_ADMIN_EMAILS, 'nathan@keyflo.ai'];

export const DEV_NAV_ITEMS = [
    { label: 'Demos', path: '/dev/demos', demosAccess: true },
    { label: 'Emails', path: '/dev/emails' },
    { label: 'Contracts', path: '/dev/contracts' },
    { label: 'Listings', path: '/dev/listings' },
    { label: 'Buyer Agreements', path: '/dev/buyer-agreements' },
    { label: 'Key People', path: '/dev/key_people' },
    { label: 'Milestones', path: '/dev/milestones' },
    { label: 'Tasks', path: '/dev/tasks' },
    { label: 'Loans', path: '/dev/loans' },
    { label: 'Referral Agreements', path: '/dev/referral_agreements' },
    { label: 'Compliance', path: '/dev/compliance' },
    { label: 'Split Attachments', path: '/dev/splitattachments' },
    { label: 'Signature Eval', path: '/dev/signature-eval' },
    { label: 'Inspection', path: '/dev/inspection' },
    { label: 'Repairs', path: '/dev/repairs' },
    { label: 'Fees', path: '/dev/fees' },
    { label: 'Users', path: '/dev/users', separator: true },
    { label: 'User Analytics', path: '/dev/user_analytics' },
    { label: 'Surveys', path: '/dev/surveys' },
    { label: 'Revenue', path: '/dev/revenue', revenueAdminOnly: true },
    { label: 'Sent Emails', path: '/dev/sent_emails' },
    { label: 'Pending Emails', path: '/dev/pending_emails' },
    { label: 'Leads', path: '/dev/leads' },
    { label: 'Waitlist', path: '/dev/waitlist' },
    { label: 'SkySlope Viewer', path: '/dev/skyslope', separator: true },
    { label: 'SkySlope Checklists', path: '/dev/skyslope_checklist' },
    { label: 'SkySlope Discrepancies', path: '/dev/skyslope_discrepancies' },
];

export function canAccessDevDemos(userEmail) {
    return DEMO_CONSOLE_EMAILS.includes((userEmail || '').toLowerCase());
}

export function getDevNavItems(userEmail) {
    const email = (userEmail || '').toLowerCase();
    const canViewRevenue = REVENUE_ADMIN_EMAILS.includes(email);
    const isNathanOnly = email === 'nathan@keyflo.ai';
    if (isNathanOnly) {
        return DEV_NAV_ITEMS.filter((item) => item.demosAccess);
    }
    return DEV_NAV_ITEMS.filter((item) => {
        if (item.revenueAdminOnly && !canViewRevenue) return false;
        return true;
    });
}

/**
 * Header hamburger → Developer entry.
 *
 * Safari (and tight mouse paths) could not reach the flyout when it sat across a
 * margin gap from "Developer". Click opens (does not toggle-close after hover);
 * single-item accounts (nathan@) go straight to that page — no flyout required.
 */
export function DeveloperMenuItem({ userEmail, onNavigate }) {
    const items = getDevNavItems(userEmail);
    const [open, setOpen] = useState(false);
    const closeTimerRef = useRef(null);

    useEffect(() => () => {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    }, []);

    if (!items.length) return null;

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimer();
        // Brief delay so the pointer can cross into the flyout without a hover gap kill.
        closeTimerRef.current = setTimeout(() => setOpen(false), 200);
    };

    const go = (path) => {
        clearCloseTimer();
        setOpen(false);
        onNavigate(path);
    };

    // nathan@ (Demos-only): one click, no flyout — avoids Safari hover dead-zone entirely.
    if (items.length === 1) {
        return (
            <button
                type="button"
                className="menu-item"
                onClick={() => go(items[0].path)}
            >
                Developer · {items[0].label}
            </button>
        );
    }

    return (
        <div
            className={`menu-item has-submenu${open ? ' is-open' : ''}`}
            onMouseEnter={() => {
                clearCloseTimer();
                setOpen(true);
            }}
            onMouseLeave={scheduleClose}
        >
            <button
                type="button"
                className="submenu-trigger"
                aria-expanded={open}
                aria-haspopup="true"
                onMouseEnter={clearCloseTimer}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearCloseTimer();
                    // Hover may already have opened the flyout; a click must not
                    // toggle it closed (mouseenter → click would otherwise close).
                    setOpen(true);
                }}
            >
                <span>Developer</span>
                <span className="submenu-chevron" aria-hidden="true">◀</span>
            </button>
            {open && (
                <div
                    className="dropdown-menu submenu-flyout"
                    onMouseEnter={clearCloseTimer}
                    onMouseLeave={scheduleClose}
                >
                    {items.map((item) => (
                        <button
                            key={item.path}
                            type="button"
                            className="menu-item"
                            onClick={() => go(item.path)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const DeveloperNavigation = () => {
    const navigate = useNavigate();
    const { user } = useAuth0();
    const [showDevMenu, setShowDevMenu] = useState(false);
    const menuItems = getDevNavItems(user?.email);

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setShowDevMenu(!showDevMenu)}
                style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                aria-label="Developer Menu"
            >
                <div style={{ width: '20px', height: '2px', background: '#374151' }} />
                <div style={{ width: '20px', height: '2px', background: '#374151' }} />
                <div style={{ width: '20px', height: '2px', background: '#374151' }} />
            </button>
            {showDevMenu && (
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        marginTop: '4px',
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        minWidth: '180px',
                        zIndex: 1000,
                    }}
                    onMouseLeave={() => setShowDevMenu(false)}
                >
                    {menuItems.map((item, index) => (
                        <button
                            key={item.path}
                            onClick={() => { setShowDevMenu(false); navigate(item.path); }}
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#374151',
                                borderBottom: (item.separator || index < menuItems.length - 1) ? '1px solid #e5e7eb' : 'none',
                                borderTop: item.separator ? '1px solid #e5e7eb' : 'none',
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DeveloperNavigation;
